import {FALLBACK_ROOM_ID} from './constants'
import type {
  CurrentTask,
  GiftInfo,
  RewardRecordItem,
  RewardRecordsResponse,
  RListItem,
  StayInfo,
  StayRewardInfo,
  StayRewardItem,
  UnionRTaskPage,
} from './types'

function toNum(value: unknown, fallback = 0): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function toStr(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value
  }
  if (value == null) {
    return fallback
  }
  return String(value)
}

function toBool(value: unknown): boolean {
  return Boolean(value)
}

function asRecord(value: unknown): {[key: string]: unknown} | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as {[key: string]: unknown}
  }
  return null
}

function asList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function resolveRoomIdFromOtherInfo(otherInfo: unknown): number {
  const obj = asRecord(otherInfo)
  if (!obj) {
    return FALLBACK_ROOM_ID
  }
  const id = Number(obj.room_id ?? obj.roomId ?? 0)
  return id > 0 ? id : FALLBACK_ROOM_ID
}

export function resolveMidFromOtherInfo(otherInfo: unknown): number {
  const obj = asRecord(otherInfo)
  if (!obj) {
    return 0
  }
  const id = Number(obj.mid ?? 0)
  return id > 0 ? id : 0
}

export function resolveSelectedMidFromList(
  list: {mid: number}[],
  currentMid: number,
  otherInfo: unknown
): number {
  if (list.length === 0) {
    return 0
  }
  if (currentMid > 0 && list.some((item) => item.mid === currentMid)) {
    return currentMid
  }
  const envMid = resolveMidFromOtherInfo(otherInfo)
  if (envMid > 0) {
    const matched = list.find((item) => item.mid === envMid)
    if (matched) {
      return matched.mid
    }
  }
  return list[0].mid
}

export function formatPearlDisplay(value: number): string {
  if (value >= 100000) {
    return `${Math.round(value / 1000)}k`
  }
  return String(value)
}

function pad2(n: number): string {
  if (n < 10) {
    return `0${n}`
  }
  return String(n)
}

export function formatHms(totalSec: number): [string, string, string] {
  const safe = totalSec > 0 ? totalSec : 0
  const h = Math.floor(safe / 3600)
  const m = Math.floor((safe % 3600) / 60)
  const s = safe % 60
  return [pad2(h), pad2(m), pad2(s)]
}

export function formatStayMmSs(elapsedSec: number): string {
  const safe = elapsedSec > 0 ? elapsedSec : 0
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${pad2(m)}:${pad2(s)}`
}

export function formatGrantTs(grantTs: number): string {
  const date = new Date(grantTs * 1000)
  const utc = date.getTime() + date.getTimezoneOffset() * 60000
  const p3 = new Date(utc + 3 * 3600 * 1000)
  const y = p3.getFullYear()
  const mo = p3.getMonth() + 1
  const d = p3.getDate()
  const hh = pad2(p3.getHours())
  const mm = pad2(p3.getMinutes())
  const ss = pad2(p3.getSeconds())
  return `${y}-${mo}-${d} ${hh}:${mm}:${ss} Utc+3`
}

export function buildChatScheme(mid: number): string {
  return `saada://openChat?uid=${mid}&from=union_r&collapse_room=1`
}

export function buildInviteScheme(roomId: number, mid: number): string {
  return `hiya://inviteRoom?room_id=${roomId}&t_mid=${mid}&reunion_from=chatpage_msg`
}

export function normalizeStayRewardItem(raw: unknown): StayRewardItem {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      reward_type: 0,
      icon: '',
      text: '',
      num: 0,
    }
  }
  return {
    reward_type: toNum(obj.reward_type),
    icon: toStr(obj.icon),
    text: toStr(obj.text),
    num: toNum(obj.num),
  }
}

export function normalizeStayRewardInfo(raw: unknown): StayRewardInfo {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      title: '',
      desc: '',
      bg_url: '',
      rewards: [],
    }
  }
  return {
    title: toStr(obj.title),
    desc: toStr(obj.desc),
    bg_url: toStr(obj.bg_url),
    rewards: asList(obj.rewards).map(normalizeStayRewardItem),
  }
}

export function normalizeCurrentTask(raw: unknown): CurrentTask {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      kind: '',
      title: '',
      countdown_sec: 0,
      btn_type: 0,
      btn_text: '',
    }
  }
  return {
    kind: toStr(obj.kind),
    title: toStr(obj.title),
    countdown_sec: toNum(obj.countdown_sec),
    btn_type: toNum(obj.btn_type),
    btn_text: toStr(obj.btn_text),
  }
}

export function normalizeStayInfo(raw: unknown): StayInfo | undefined {
  const obj = asRecord(raw)
  if (!obj) {
    return undefined
  }
  const stay: StayInfo = {
    stay_sec: toNum(obj.stay_sec),
    stay_connect_sec: toNum(obj.stay_connect_sec),
    stay_already: toNum(obj.stay_already),
    connect_pearl: toNum(obj.connect_pearl),
    reward_info: normalizeStayRewardInfo(obj.reward_info),
  }
  if (obj.stay_join_ts != null && obj.stay_join_ts !== '') {
    stay.stay_join_ts = toNum(obj.stay_join_ts)
  }
  return stay
}

export function normalizeGiftInfo(raw: unknown): GiftInfo {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      locked: true,
      title: '',
      unlock_hint: '',
      task_type: '',
      gift_got: 0,
      gift_target: 0,
      gift_percent: 0,
      gift_expect_pearl: 0,
      anchor_gift_got: 0,
    }
  }
  return {
    locked: toBool(obj.locked),
    title: toStr(obj.title),
    unlock_hint: toStr(obj.unlock_hint),
    task_type: toStr(obj.task_type),
    gift_got: toNum(obj.gift_got),
    gift_target: toNum(obj.gift_target),
    gift_percent: toNum(obj.gift_percent),
    gift_expect_pearl: toNum(obj.gift_expect_pearl),
    anchor_gift_got: toNum(obj.anchor_gift_got),
  }
}

export function normalizeRListItem(raw: unknown): RListItem {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      mid: 0,
      avatar: '',
      nick: '',
      vip_level: 0,
      stage: '',
      current: normalizeCurrentTask(null),
      connect: false,
      gift: normalizeGiftInfo(null),
    }
  }
  const item: RListItem = {
    mid: toNum(obj.mid),
    avatar: toStr(obj.avatar),
    nick: toStr(obj.nick),
    vip_level: toNum(obj.vip_level),
    stage: toStr(obj.stage),
    current: normalizeCurrentTask(obj.current),
    connect: toBool(obj.connect),
    gift: normalizeGiftInfo(obj.gift),
  }
  const stay = normalizeStayInfo(obj.stay)
  if (stay) {
    item.stay = stay
  }
  return item
}

export function normalizeUnionRTaskPage(raw: unknown): UnionRTaskPage {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      empty: false,
      pearl_max_display: 0,
      total_pearl: 0,
      r_list: [],
      err_msg: '',
    }
  }
  return {
    empty: toBool(obj.empty),
    pearl_max_display: toNum(obj.pearl_max_display),
    total_pearl: toNum(obj.total_pearl),
    r_list: asList(obj.r_list).map(normalizeRListItem),
    err_msg: toStr(obj.err_msg),
  }
}

export function normalizeRewardRecordItem(raw: unknown): RewardRecordItem {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      id: 0,
      reason: '',
      reward_type: 0,
      reward_name: '',
      reward_image: '',
      amount: 0,
      grant_ts: 0,
      avatar: '',
      name: '',
    }
  }
  return {
    id: toNum(obj.id),
    reason: toStr(obj.reason),
    reward_type: toNum(obj.reward_type),
    reward_name: toStr(obj.reward_name),
    reward_image: toStr(obj.reward_image),
    amount: toNum(obj.amount),
    grant_ts: toNum(obj.grant_ts),
    avatar: toStr(obj.avatar),
    name: toStr(obj.name),
  }
}

export function normalizeRewardRecords(raw: unknown): RewardRecordsResponse {
  const obj = asRecord(raw)
  if (!obj) {
    return {
      list: [],
      has_more: false,
      err_msg: '',
    }
  }
  return {
    list: asList(obj.list).map(normalizeRewardRecordItem),
    has_more: toBool(obj.has_more),
    err_msg: toStr(obj.err_msg),
  }
}
